import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import { FollowSignalsContext } from "@shared/features/signals/common/FollowSignals.context";
import {
  FollowSignalData,
  OneClickFollowBotData,
  SymbolClientsData,
} from "@shared/types";
import {
  FollowSignalType,
  SignalFollowModalProps,
} from "@shared/features/signals/common/types";
import { sharedLib } from "@shared/services/shared.service";
import { signalsSelectors } from "@shared/slices/signals.slice";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";
import { getListItemFollowData } from "@shared/features/signals/common/utils";
import { EXCHANGES } from "@shared/common/constants";

interface SignalsWrapperProps {
  SignalFollowModal: FC<SignalFollowModalProps>;
}

const FollowSignalProvider = ({
  SignalFollowModal,
  children,
}: PropsWithChildren<SignalsWrapperProps>) => {
  const [followModalType, setFollowModalType] = useState<{
    followType: FollowSignalType;
    signal: FollowSignalData;
  } | void>();
  const { signal, followType } = followModalType || {};

  const options = { refetchOnMountOrArgChange: true };
  const getExchangeSignalIdsStatus =
    sharedLib.sharedApi.signals.useGetExchangeSignalIdsQuery(
      signal
        ? {
            signal_id: signal.signal_id,
            for_admin: false,
          }
        : skipToken,
      options
    );
  const getClientsDataFromSymbolStatus =
    sharedLib.sharedApi.trades.useGetClientsDataFromSymbolQuery(
      signal ? { symbol: signal?.symbol } : skipToken,
      options
    );
  const getBotsOneClickDataStatus =
    sharedLib.sharedApi.bots.useGetBotsOneClickDataQuery(
      signal ? undefined : skipToken,
      options
    );
  const groupData = useSelector(
    signalsSelectors.selectSubGroupData(signal?.sub_group_id)
  );

  const openFollowModal = (
    signal: FollowSignalData,
    followType: FollowSignalType
  ) => {
    setFollowModalType({ signal, followType });
  };

  const exchangeSignalIds = getExchangeSignalIdsStatus.data;
  const clientsData = getClientsDataFromSymbolStatus.data;
  const clientIdToData = useMemo(() => {
    return clientsData?.reduce((acc, clientData) => {
      acc[clientData.id] = clientData;
      return acc;
    }, {} as Record<number, SymbolClientsData>);
  }, [clientsData]);

  const getBotAccountData = useCallback(
    (bot?: OneClickFollowBotData) => {
      return bot ? clientIdToData?.[bot?.client_id] : undefined;
    },
    [clientIdToData]
  );

  const getItemErrorMessage = (
    _account?: SymbolClientsData,
    bot?: OneClickFollowBotData
  ) => {
    const account = _account ?? getBotAccountData(bot);
    if (!signal || !account || !clientIdToData) return;
    const exchangeName =
      account.exchange === EXCHANGES.BINANCE
        ? "Binance Spot"
        : account.exchange;
    if (!account.can_trade) {
      return `This signal's symbol (${
        signal.symbol
      }) is not available on the selected ${
        bot ? "bot's " : ""
      }exchange (${exchangeName})`;
    }
    const selectedExchangeText = `the selected ${
      bot ? "bot's " : ""
    }exchange (${exchangeName}).`;
    if (exchangeSignalIds && !(account.exchange in exchangeSignalIds))
      return `This signal wasn't published for ${selectedExchangeText}`;
    const followData = getListItemFollowData(signal, _account, bot);
    const baseObjectName = bot ? "bot" : "exchange";
    switch (followType) {
      case FollowSignalType.FOLLOW:
        if (!followData.allowFollow) {
          if (followData.tradeExists || followData.requiresUpdate) {
            return `There is already an open trade for this signal on the selected ${baseObjectName}`;
          } else {
            return `This signal is no longer active on ${selectedExchangeText}`;
          }
        }
        break;
      case FollowSignalType.UPDATE:
        if (!followData.requiresUpdate) {
          if (followData.tradeExists)
            return "Your open trade following this signal is already updated";
          else
            return `No trade is open for this signal on this ${baseObjectName}`;
        }
        break;
      case FollowSignalType.CANCEL:
        if (!followData.requiresCancel) {
          if (!followData.tradeExists)
            return `No trade is open for this signal on this ${baseObjectName}`;
        }
    }
  };

  const isFetching =
    getClientsDataFromSymbolStatus.isFetching ||
    getExchangeSignalIdsStatus.isFetching ||
    getBotsOneClickDataStatus.isFetching;
  const isLoading =
    getClientsDataFromSymbolStatus.isLoading ||
    getExchangeSignalIdsStatus.isLoading ||
    getBotsOneClickDataStatus.isLoading;

  return (
    <FollowSignalsContext.Provider
      value={{
        openFollowModal,
        getClientsDataFromSymbolStatus,
        groupData,
        getExchangeSignalIdsStatus,
        getBotsOneClickDataStatus,
        isFetching,
        isLoading,
        getItemErrorMessage,
        signal,
        followType,
        getBotAccountData,
      }}
    >
      <>
        {children}
        <SignalFollowModal
          visible={!!followModalType}
          closeModal={() => {
            setFollowModalType();
          }}
          signal={followModalType?.signal}
          followType={followModalType?.followType}
        />
      </>
    </FollowSignalsContext.Provider>
  );
};

export default FollowSignalProvider;
